import { Combobox, type CreatableOption } from '@/global/components/Combobox'
import type { FC, ReactNode, Ref } from 'react'
import {
    Controller,
    useFormContext,
    type UseControllerProps,
} from 'react-hook-form'

type FormFieldAutocompleteProps = Omit<
    UseControllerProps,
    'control' | 'defaultValue'
> & {
    name: string
    options: ReadonlyArray<any>
    label?: ReactNode
    /** Ref do gatilho — para rolar a tela até o campo quando a URL pede foco. */
    triggerRef?: Ref<HTMLDivElement>
    placeholder?: string
    getOptionLabel?: (option: any) => string
    /** Chave React de cada opção. Use quando dois rótulos puderem ser iguais. */
    getOptionKey?: (option: any) => string
    /** Conteúdo de cada opção da lista. Padrão: só o rótulo. */
    renderOption?: (option: any) => ReactNode
    creatableOption?: CreatableOption
    multiple?: boolean
    /** Quantos chips aparecem no gatilho antes de virarem um `+N`. */
    limitTags?: number
    disabled?: boolean
    required?: boolean
    /**
     * Não reserva a linha auxiliar abaixo do campo. Padrão `false`: campo de
     * formulário reserva o espaço, para que a mensagem de erro não empurre o
     * formulário quando aparece.
     */
    unHelperText?: boolean
    helperText?: ReactNode
    size?: 'small' | 'medium'
    variant?: 'filled' | 'outlined'
    onInputChange?: (inputValue: string) => void
    /**
     * Busca controlada. Definido, o filtro passa a ser responsabilidade de quem
     * chama (servidor) e o `cmdk` para de filtrar.
     */
    inputValue?: string
    /** Mostra o giro de carregando no lugar da lista. */
    loading?: boolean
    /**
     * Chamado com a opção escolhida, depois de gravá-la no formulário. Recebe
     * sempre o valor da opção — nunca o resultado de `paraFormulario`.
     */
    onChange?: (value: any) => void
    noOptionsText?: string
    /** Placeholder da busca dentro do popover. */
    searchPlaceholder?: string
    /** Conteúdo à direita do campo, antes da seta — ícone de ajuda, etc. */
    endAdornment?: ReactNode
    isOptionEqualToValue?: (option: any, value: any) => boolean
    /** Converte o valor guardado no formulário na opção exibida pelo campo. */
    paraCampo?: (valorDoFormulario: any) => any
    /** Converte a opção escolhida no valor que vai para o formulário. */
    paraFormulario?: (opcao: any) => any
}

/**
 * `Combobox` ligado ao react-hook-form. Toda a aparência mora lá; aqui ficam o
 * `Controller`, a herança de `disabled` do formulário e o erro do Zod na linha
 * auxiliar.
 *
 * O par `paraCampo`/`paraFormulario` existe para quando o formulário guarda algo
 * diferente da opção — só o `id`, por exemplo: `paraCampo` reidrata a opção a
 * partir do que está gravado, `paraFormulario` reduz a opção escolhida ao que
 * deve ser gravado. Sem eles, o valor gravado é a opção inteira.
 *
 * Escolha entre este e o `FormFieldSelect` pela necessidade de busca, seleção
 * múltipla ou criação — ver o README de `global/components`.
 */
export const FormFieldAutocomplete: FC<FormFieldAutocompleteProps> = ({
    name,
    rules,
    options,
    label,
    triggerRef,
    placeholder,
    getOptionLabel,
    getOptionKey,
    renderOption,
    variant = 'filled',
    size = 'medium',
    creatableOption,
    multiple = false,
    limitTags,
    unHelperText = false,
    shouldUnregister,
    onInputChange,
    inputValue,
    loading,
    onChange,
    noOptionsText = 'Nenhuma opção encontrada',
    searchPlaceholder,
    endAdornment,
    isOptionEqualToValue,
    paraCampo,
    paraFormulario,
    helperText,
    required,
    disabled: disabledProp,
}) => {
    const { control } = useFormContext()
    const disabled = disabledProp || (control as any)._options?.disabled

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            shouldUnregister={shouldUnregister}
            render={({
                field: { onChange: onChangeCampo, value },
                fieldState: { error },
            }) => {
                const emitir = (novoValor: any) => {
                    onChangeCampo(
                        paraFormulario ? paraFormulario(novoValor) : novoValor,
                    )
                    onChange?.(novoValor)
                }

                const valorDoCampo = paraCampo ? paraCampo(value) : value

                return multiple ? (
                    <Combobox<any>
                        multiple
                        triggerRef={triggerRef}
                        label={label}
                        placeholder={placeholder}
                        options={options}
                        value={valorDoCampo ?? []}
                        onChange={emitir}
                        getOptionLabel={getOptionLabel}
                        getOptionKey={getOptionKey}
                        renderOption={renderOption}
                        isOptionEqualToValue={isOptionEqualToValue}
                        creatableOption={creatableOption}
                        limitTags={limitTags}
                        onInputChange={onInputChange}
                        inputValue={inputValue}
                        loading={loading}
                        noOptionsText={noOptionsText}
                        searchPlaceholder={searchPlaceholder}
                        endAdornment={endAdornment}
                        variant={variant}
                        size={size}
                        disabled={disabled}
                        required={required}
                        error={!!error}
                        unHelperText={unHelperText}
                        helperText={error?.message ?? helperText}
                    />
                ) : (
                    <Combobox<any>
                        triggerRef={triggerRef}
                        label={label}
                        placeholder={placeholder}
                        options={options}
                        value={valorDoCampo ?? null}
                        onChange={emitir}
                        getOptionLabel={getOptionLabel}
                        getOptionKey={getOptionKey}
                        renderOption={renderOption}
                        isOptionEqualToValue={isOptionEqualToValue}
                        creatableOption={creatableOption}
                        limitTags={limitTags}
                        onInputChange={onInputChange}
                        inputValue={inputValue}
                        loading={loading}
                        noOptionsText={noOptionsText}
                        searchPlaceholder={searchPlaceholder}
                        endAdornment={endAdornment}
                        variant={variant}
                        size={size}
                        disabled={disabled}
                        required={required}
                        error={!!error}
                        unHelperText={unHelperText}
                        helperText={error?.message ?? helperText}
                    />
                )
            }}
        />
    )
}
